import React from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Radio,
  TextField,
  Chip,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Divider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ---------------- STEPS DATA ---------------- */
const steps = [
  { label: "Validity Period", sub: "Financial year" },
  { label: "Calculation Rules", sub: "Define slabs" },
  { label: "Eligibility", sub: "Optional constraints" },
  { label: "CP Sign-Off", sub: "Configure window" },
  { label: "Exclusions", sub: "POP projects" },
  { label: "Review", sub: "Preview & save" },
];

export default function AOPCalculationRules({ data, setData, onBack, onNext }) {
  const {
    targetBasis = "revenue",
    incentiveType = "percentage",
    targetSlabs = [{ from: "", to: "", incentive: "" }],
  } = data;

  const [slabErrors, setSlabErrors] = React.useState([]);

const isValueBased = targetBasis === "revenue";
const isPercentage = incentiveType === "percentage";

  /* ---------------- VALIDATION ---------------- */

  const validateSlabs = () => {
    let errors = [];

    const parsedSlabs = targetSlabs.map((slab, index) => {
      if (slab.from === "" || slab.to === "" || slab.incentive === "") {
        errors[index] = "All fields are required";
        return null;
      }

      const from = Number(slab.from);
      const to = Number(slab.to);
      const incentive = Number(slab.incentive);

      if (isNaN(from) || isNaN(to) || isNaN(incentive)) {
        errors[index] = "Values must be numbers only";
        return null;
      }

      if (from >= to) {
        errors[index] = "'From' must be less than 'To'";
        return null;
      }

      return { from, to };
    });

    const validSlabs = parsedSlabs.filter(Boolean);

    const sorted = [...validSlabs].sort((a, b) => a.from - b.from);

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].from < sorted[i - 1].to) {
        const overlapIndex = targetSlabs.findIndex(
          (s) =>
            Number(s.from) === sorted[i].from && Number(s.to) === sorted[i].to,
        );
        errors[overlapIndex] = "Slab overlaps with previous range";
      }
    }

    return errors;
  };

  /* ---------------- SLAB HANDLERS ---------------- */

  const addSlab = () => {
    setData({
      ...data,
      targetSlabs: [...targetSlabs, { from: "", to: "", incentive: "" }],
    });
  };

  const updateSlab = (index, field, value) => {
    const updated = [...targetSlabs];
    updated[index][field] = value;

    setData({
      ...data,
      targetSlabs: updated,
    });
  };

  const deleteSlab = (index) => {
    const updated = targetSlabs.filter((_, i) => i !== index);

    setData({
      ...data,
      targetSlabs: updated.length
        ? updated
        : [{ from: "", to: "", incentive: "" }],
    });
  };

  const handleNext = () => {
    const errors = validateSlabs();
    setSlabErrors(errors);

    const hasError = errors.some(Boolean);

    if (!hasError) {
      onNext();
    }
  };
  const resetSlabs = () => {
    setData({
      ...data,
      targetSlabs: [{ from: "", to: "", incentive: "" }],
    });
  };
  const formatIndianNumber = (value) => {
  if (!value) return "";
  const number = value.toString().replace(/,/g, "");
  if (isNaN(number)) return value;

  return Number(number).toLocaleString("en-IN");
};


const cleanNumber = (value) => {
  return value.replace(/[^0-9]/g, "");
};

const cleanDecimalNumber = (value) => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
};
  return (
    <Box sx={{ p: 1, minHeight: "100vh" }}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{
          mb: 3,
          cursor: "pointer",
          color: "text.secondary",
          fontSize: 14,
          fontWeight: 500,
        }}
        onClick={onBack}
      >
        ← Back to AOPs
      </Typography>

      {/* Stepper */}

      {/* Stepper */}
      <Stepper
        activeStep={1}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepLabel-label": {
            fontSize: "0.95rem",
            fontWeight: 500,
          },
          "& .MuiStepIcon-root": {
            fontSize: 32,
          },
          "& .Mui-active": {
            color: "#2563EB",
          },
          "& .Mui-completed": {
            color: "#2563EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Box textAlign="center">
                <Typography fontSize="0.95rem" fontWeight={600}>
                  {step.label}
                </Typography>
                <Typography fontSize="0.75rem" color="text.secondary">
                  {step.sub}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Info */}
      <Box
        sx={{
          background: "#EFF6FF",
          border: "1px solid #BFDBFE",
          borderRadius: 10,
          p: 2,
          display: "flex",
          gap: 1.5,
          mb: 3,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#2563EB", mt: "2px" }} />
        <Typography fontSize={13} color="#1E3A8A">
          <b>AOP Achievement Logic</b>
          <br />
          AOP achievement is calculated only on successful agreements.
        </Typography>
      </Box>

      {/* Rule Type (UNCHANGED) */}
      <Card sx={cardStyle}>
        <Typography fontSize={14} fontWeight={600} mb={2}>
          Rule Type
        </Typography>

        <Typography fontSize={13} fontWeight={500} mb={1}>
          Calculation Basis
        </Typography>

        <Box display="flex" gap={2} mb={3}>
          <OptionCard
            selected={isValueBased}
            title="Agreement Value Based"
            desc="Calculate based on total agreement value"
            onClick={() => {
              if (targetBasis !== "revenue") {
                setData({
                  ...data,
                 targetBasis: "revenue",
                  targetSlabs: [{ from: "", to: "", incentive: "" }],
                });
              }
            }}
          />

          <OptionCard
            selected={!isValueBased}
            title="Number of Agreements"
            desc="Calculate based on agreement count"
            onClick={() => {
              if (targetBasis !== "count") {
                setData({
                  ...data,
                 targetBasis: "count",
                  targetSlabs: [{ from: "", to: "", incentive: "" }],
                });
              }
            }}
          />
        </Box>

        <Typography fontSize={13} fontWeight={500} mb={1}>
          Reward Type
        </Typography>

        <Box display="flex" gap={2}>
          <OptionCard
            selected={isPercentage}
            title="Percentage on Value"
            desc="Percentage of agreement value"
            onClick={() => {
              if (incentiveType !== "percentage") {
                setData({
                  ...data,
                  incentiveType: "percentage",
                  targetSlabs: [{ from: "", to: "", incentive: "" }],
                });
              }
            }}
          />

          <OptionCard
            selected={!isPercentage}
            title="Fixed Amount"
            desc="Fixed amount per slab"
            onClick={() => {
              if (incentiveType !== "fixed") {
                setData({
                  ...data,
                  incentiveType: "fixed",
                  targetSlabs: [{ from: "", to: "", incentive: "" }],
                });
              }
            }}
          />
        </Box>
      </Card>

      {/* Range Rules */}
      <Card sx={cardStyle}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontSize={14} fontWeight={600}>
            Range Rules
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addSlab}
            sx={{ textTransform: "none" }}
          >
            Add Rule
          </Button>
        </Box>

        {targetSlabs.map((slab, index) => (
          <Box key={index}>
         <RangeRow
  index={index + 1}
  slab={slab}
  isValueBased={isValueBased}
  isPercentage={isPercentage}
  onChange={updateSlab}
  onDelete={deleteSlab}
  formatIndianNumber={formatIndianNumber}
  cleanNumber={cleanNumber}
  cleanDecimalNumber={cleanDecimalNumber}
/>
            {slabErrors[index] && (
              <Typography color="error" fontSize={12} mt={-1} mb={1}>
                {slabErrors[index]}
              </Typography>
            )}
          </Box>
        ))}
      </Card>

      {/* Footer */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={handleNext}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

/* ---------------- STYLES ---------------- */

const cardStyle = {
  border: "1px solid #E5E7EB",
  borderRadius: 12,
  boxShadow: "none",
  p: 3,
  mb: 3,
};

/* ---------------- SUB COMPONENTS ---------------- */

function OptionCard({ selected, title, desc, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        border: selected ? "1.5px solid #2563EB" : "1px solid #E5E7EB",
        borderRadius: 10,
        p: 2,
        display: "flex",
        gap: 1.5,
        cursor: "pointer",
        background: selected ? "#EFF6FF" : "#fff",
      }}
    >
      <Radio checked={selected} />
      <Box>
        <Typography fontSize={13} fontWeight={500}>
          {title}
        </Typography>
        <Typography fontSize={12} color="#6B7280">
          {desc}
        </Typography>
      </Box>
    </Box>
  );
}

function RangeRow({
  index,
  slab,
  isValueBased,
  isPercentage,
  onChange,
  onDelete,
  formatIndianNumber,
  cleanNumber,
  cleanDecimalNumber,
}){
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <Chip label={index} size="small" />
<TextField
  type="text"
  label={isValueBased ? "From Value" : "From Agreements"}
  size="small"
  fullWidth
  value={formatIndianNumber(slab.from)}
  onChange={(e) =>
    onChange(index - 1, "from", cleanNumber(e.target.value))
  }
/>

     <TextField
  type="text"
  label={isValueBased ? "To Value (Cr)" : "To Agreements"}
  size="small"
  fullWidth
  value={formatIndianNumber(slab.to)}
  onChange={(e) =>
    onChange(index - 1, "to", cleanNumber(e.target.value))
  }
/>

      <TextField
  type="text"
  label={isPercentage ? "Brokerage %" : "Amount (₹)"}
  size="small"
  fullWidth
  value={
    isPercentage
      ? slab.incentive
      : formatIndianNumber(slab.incentive)
  }
  onChange={(e) =>
    onChange(
      index - 1,
      "incentive",
      isPercentage
        ? cleanDecimalNumber(e.target.value)
        : cleanNumber(e.target.value)
    )
  }
/>

      <IconButton onClick={() => onDelete(index - 1)}>
        <DeleteOutlineIcon />
      </IconButton>
    </Box>
  );
}
